import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Star, PenLine, Award, MessageSquareReply, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2.5 text-[14px] outline-none focus:border-brand';
const fmtDate = (iso) => new Date(iso).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });

export const Stars = ({ value, size = 16, className = '' }) => (
  <span className={`inline-flex gap-0.5 ${className}`} aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(i => {
      const fill = Math.max(0, Math.min(1, value - i + 1));
      return (
        <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
          <Star size={size} className="absolute inset-0 text-neutral-300 dark:text-neutral-700" />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}><Star size={size} className="text-amber-400 fill-amber-400" /></span>
        </span>
      );
    })}
  </span>
);

const StarInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex gap-1" onMouseLeave={() => setHover(0)} data-testid="review-star-input">
      {[1, 2, 3, 4, 5].map(i => (
        <button type="button" key={i} data-testid={`review-star-${i}`} onMouseEnter={() => setHover(i)} onClick={() => onChange(i)} aria-label={`${i} stars`} className="transition-transform hover:scale-110">
          <Star size={28} className={(hover || value) >= i ? 'text-amber-400 fill-amber-400' : 'text-neutral-300 dark:text-neutral-700'} />
        </button>
      ))}
    </div>
  );
};

export const Summary = ({ s, onWrite, hasMine }) => (
  <div data-testid="reviews-summary" className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-6 sm:p-8 grid gap-8 md:grid-cols-[auto,1fr,auto] items-center">
    <div className="text-center md:text-left">
      <div data-testid="reviews-average" className="font-serif text-6xl text-neutral-900 dark:text-neutral-100">{s.count ? s.average.toFixed(1) : '—'}</div>
      <Stars value={s.average} size={20} className="mt-2" />
      <div data-testid="reviews-count" className="mt-1.5 text-[13px] text-neutral-500">{s.count} review{s.count === 1 ? '' : 's'}</div>
    </div>
    <div className="space-y-1.5">
      {[5, 4, 3, 2, 1].map(n => {
        const c = s.distribution[String(n)] || 0;
        return (
          <div key={n} className="flex items-center gap-3 text-[12.5px]">
            <span className="w-3 text-neutral-500">{n}</span>
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <div className="flex-1 h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
              <div className="h-full rounded-full bg-amber-400 transition-[width] duration-700" style={{ width: s.count ? `${(c / s.count) * 100}%` : 0 }} />
            </div>
            <span className="w-6 text-right text-neutral-500">{c}</span>
          </div>
        );
      })}
    </div>
    <button data-testid="write-review-btn" onClick={onWrite} className="btn-brand inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold">
      <PenLine size={16} /> {hasMine ? 'Edit your review' : 'Write a review'}
    </button>
  </div>
);

const ReviewForm = ({ initial, onSaved, onCancel, headers }) => {
  const [f, setF] = useState({ rating: initial?.rating || 0, name: initial?.name || '', title: initial?.title || '', text: initial?.text || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { setErr('Please choose a star rating'); return; }
    setBusy(true); setErr('');
    try { onSaved((await axios.post(`${API}/reviews`, f, { headers })).data); }
    catch (e2) { setErr(e2?.response?.data?.detail || 'Could not save your review'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="review-form" className="rounded-2xl border border-brand/40 bg-white dark:bg-[#141416] p-6 space-y-4 page-fade-in">
      <div>
        <span className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Your rating</span>
        <StarInput value={f.rating} onChange={(v) => setF(p => ({ ...p, rating: v }))} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="review-name-input" className={inputCls} placeholder="Display name (e.g. Sam K.)" maxLength={40} value={f.name} onChange={(e) => setF(p => ({ ...p, name: e.target.value }))} />
        <input data-testid="review-title-input" className={inputCls} placeholder="Headline (optional)" maxLength={100} value={f.title} onChange={(e) => setF(p => ({ ...p, title: e.target.value }))} />
      </div>
      <textarea data-testid="review-text-input" rows={4} className={inputCls + ' resize-none'} placeholder="Tell others about your experience: service, delivery, quality…" maxLength={1500} value={f.text} onChange={(e) => setF(p => ({ ...p, text: e.target.value }))} />
      {err && <p data-testid="review-form-error" className="text-[12.5px] text-red-600">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
        <button data-testid="review-submit-btn" disabled={busy} className="btn-brand inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13.5px] font-semibold disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Post review</button>
      </div>
    </form>
  );
};

export const ReviewCard = ({ r, i }) => (
  <article data-testid={`review-card-${r.id}`} style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
    className={`page-fade-in rounded-2xl border bg-white dark:bg-[#141416] p-6 ${r.is_featured ? 'border-amber-300 dark:border-amber-700/60 shadow-[0_10px_30px_-15px_rgba(245,158,11,0.45)]' : 'border-neutral-200 dark:border-neutral-800'}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-brand-soft text-brand font-semibold">{(r.name || '?').charAt(0).toUpperCase()}</span>
        <div>
          <div className="font-medium text-[14px] text-neutral-900 dark:text-neutral-100">{r.name}</div>
          <div className="text-[11.5px] text-neutral-500">{fmtDate(r.created_at)}{r.updated_at ? ' · edited' : ''}</div>
        </div>
      </div>
      {r.is_featured && <span data-testid={`review-featured-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2.5 py-1 text-[11px] font-semibold"><Award size={12} /> Featured</span>}
    </div>
    <Stars value={r.rating} size={15} className="mt-4" />
    {r.title && <h3 className="mt-2 font-semibold text-[15px] text-neutral-900 dark:text-neutral-100">{r.title}</h3>}
    <p className="mt-1.5 text-[14px] leading-relaxed text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{r.text}</p>
    {r.reply && (
      <div data-testid={`review-reply-${r.id}`} className="mt-4 rounded-xl bg-neutral-50 dark:bg-neutral-900 border-l-2 border-brand px-4 py-3">
        <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-brand"><MessageSquareReply size={13} /> Reply from Healsglobe Exports</div>
        <p className="mt-1 text-[13.5px] leading-relaxed text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{r.reply}</p>
      </div>
    )}
  </article>
);

const ReviewsPage = () => {
  const { isAuthed, headers, openAuth } = useCustomerAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [mineLoaded, setMineLoaded] = useState(false);
  const [writing, setWriting] = useState(false);

  const load = useCallback(() => axios.get(`${API}/reviews`).then(({ data: d }) => setData(d)).catch(() => setData({ summary: { average: 0, count: 0, distribution: {} }, reviews: [] })), []);
  useEffect(() => { load(); document.title = 'Customer Reviews · Healsglobe Exports'; window.scrollTo(0, 0); }, [load]);
  useEffect(() => {
    setMineLoaded(false);
    if (isAuthed) axios.get(`${API}/reviews/mine`, { headers }).then(({ data: d }) => setMine(d)).catch(() => {}).finally(() => setMineLoaded(true));
    else { setMine(null); setMineLoaded(true); }
  }, [isAuthed, headers]);

  const onWrite = () => (isAuthed ? setWriting(true) : openAuth(() => setWriting(true), 'Sign in to write a review'));
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('write') !== '1' || !data) return;
    setParams({}, { replace: true });
    onWrite();
    setTimeout(() => document.querySelector('[data-testid=review-form], [data-testid=reviews-summary]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, data]);

  return (
    <main data-testid="reviews-page" className="max-w-5xl mx-auto px-6 pt-14 pb-28 sm:py-20">
      <div className="text-[11.5px] tracking-[0.35em] uppercase text-brand font-semibold">Reviews</div>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-neutral-900 dark:text-neutral-100">What our customers say</h1>
      <p className="mt-4 max-w-2xl text-[15px] text-neutral-600 dark:text-neutral-400">Honest feedback about our service, delivery and product quality.</p>
      {!data ? <div className="mt-10 h-48 rounded-2xl bg-neutral-100 dark:bg-neutral-900 animate-pulse" /> : (
        <div className="mt-10 space-y-6">
          <Summary s={data.summary} onWrite={onWrite} hasMine={!!mine} />
          {writing && mineLoaded && <ReviewForm key={mine?.id || 'new'} initial={mine} headers={headers} onCancel={() => setWriting(false)} onSaved={(r) => { setMine(r); setWriting(false); load(); }} />}
          {data.reviews.length === 0
            ? <p data-testid="reviews-empty" className="text-center text-neutral-500 py-10">No reviews yet. Be the first to share your experience!</p>
            : <div data-testid="reviews-list" className="grid gap-4 md:grid-cols-2">{data.reviews.map((r, i) => <ReviewCard key={r.id} r={r} i={i} />)}</div>}
        </div>
      )}
    </main>
  );
};

export default ReviewsPage;
